/**
 * Achievements: goofy, family-friendly pixel badges (docs/achievements.md, docs/roadmap-2026-10.md
 * `achievementsUI`). Owner, 2026-10-04: "avoid things like 'first blood' this is for families. Keep
 * it goofy and fun." Each is earned once per peon per household and pays a small one-off gold bonus
 * (a 'bonus' row in gold_ledger). Earning and paying never depend on the flag; the flag only shows
 * them (and lets the push go out).
 *
 * The rules are pure functions over facts the server reads cheaply at the moment of the event
 * (server/achievements.ts). Shared by the server, the web and the native app.
 */

/** Every badge's key, in the order they are shown. New badges go on the end. */
export const ACHIEVEMENT_KEYS = [
  "first-chore",
  "dish-wizard",
  "early-bird",
  "night-owl",
  "quick-peon",
  "sock-goblin",
  "seven-sleeps",
  "hundred-club",
  "big-spender",
  "fancy-peon",
  "gold-hoarder",
  "crowned",
  "good-human",
] as const;
export type AchievementKey = (typeof ACHIEVEMENT_KEYS)[number];

/**
 * What the server knows about a peon when it checks: counts over their record. A fact that is not
 * read for an event is null, and a rule that needs it does not fire.
 */
export interface AchievementFacts {
  /** Tasks this peon has finished, all time (this one included). */
  tasksDone: number | null;
  /** Finished tasks whose title mentions dishes (DISH_WORDS). */
  dishTasks: number | null;
  /** Finished tasks whose title mentions laundry (LAUNDRY_WORDS). */
  laundryTasks: number | null;
  /** Finished tasks whose title is an Omie job (isOmieJob: OMIE_WORDS, OMIE_WHOLE_WORDS). */
  omieTasks: number | null;
  /** Finished tasks whose bounty carried a quick bonus. */
  quickBonuses: number | null;
  /**
   * Finished tasks done in EARLY_BIRD_HOURS, local time in the household's zone. A completion's
   * check counts the recent ones (the task just done among them); a backfill counts all of them.
   */
  earlyDone: number | null;
  /** Finished tasks done in NIGHT_OWL_HOURS, counted the same way as earlyDone. */
  nightDone: number | null;
  /**
   * Days in a row with at least one task finished: for a completion, the run ending on its day; for
   * a backfill, the longest run anywhere in the record (longestStreak).
   */
  dayStreak: number | null;
  /** Wardrobe pieces bought with gold. */
  cosmeticsBought: number | null;
  /** Wardrobe pieces owned, however they came. */
  cosmeticsOwned: number | null;
  /** Months this peon has been crowned Peon of the Month. */
  crowns: number | null;
  /** Gold held right now. */
  balance: number | null;
}

/** Facts with nothing known: a starting point for an event's own. */
export const NO_FACTS: AchievementFacts = {
  tasksDone: null,
  dishTasks: null,
  laundryTasks: null,
  omieTasks: null,
  quickBonuses: null,
  earlyDone: null,
  nightDone: null,
  dayStreak: null,
  cosmeticsBought: null,
  cosmeticsOwned: null,
  crowns: null,
  balance: null,
};

export interface Achievement {
  key: AchievementKey;
  /** The badge's name, shown and pushed ("New badge: Dish Wizard"). */
  name: string;
  /** One short plain line of what earned it. */
  line: string;
  /** The one-off gold it pays, 10 to 30. */
  gold: number;
  /** Its cell in public/ui/badge-sheet.png (scripts/make-badges.mjs), left to right. */
  sprite: number;
  /** Whether the facts earn it. */
  earned: (f: AchievementFacts) => boolean;
}

/** Title words that make a task a dish job ("Do the dishes", "Empty the dishwasher"). Lowercase, matched anywhere. */
export const DISH_WORDS = ["dish"] as const;
/** Title words that make a task a laundry job. Lowercase, matched anywhere ("laundr" covers laundry and laundromat). */
export const LAUNDRY_WORDS = ["laundr", "sock", "clothes", "towel", "dryer"] as const;

/** Whether a title mentions any of `words`, ignoring case. */
export function titleMentions(words: ReadonlyArray<string>, title: string): boolean {
  const t = title.toLowerCase();
  return words.some((w) => t.includes(w));
}

/**
 * Omie is the household's dog, a husky malamute, and the app is named after him. Owner, 2026-10-05:
 * "Should have one for 'Omie jobs' Omie is a dog he's a husky malamute". Title words that make a
 * task an Omie job, lowercase, matched anywhere in the title like DISH_WORDS:
 * - "omie": his name ("Walk Omie", "Omie's bath");
 * - "dog": dog, dogs, doggy, dog food;
 * - "pup": pup, puppy, pupper;
 * - "husky", "malamute": what he is;
 * - "leash", "kibble": his walk and his dinner;
 * - "paw": paw, paws ("Wipe the paws");
 * - "poop": poop, pooper scooper.
 */
export const OMIE_WORDS = ["omie", "dog", "pup", "husky", "malamute", "leash", "kibble", "paw", "poop"] as const;
/**
 * Omie words matched as whole words only (titleMentionsWord), because anywhere in the title they
 * would catch other jobs:
 * - "walk": "Walk Omie", "Take the dog for a walk", but not "Shovel the sidewalk" or "walkway";
 * - "fur": "Brush the fur", but not "Dust the furniture" or "Change the furnace filter";
 * - "vet": "Vet at 3", but not "Vacuum the velvet couch" or "Trim the privet".
 */
export const OMIE_WHOLE_WORDS = ["walk", "fur", "vet"] as const;

/** What splits a title into words for titleMentionsWord: spaces and these. The server's SQL uses the same list. */
export const WORD_BREAKS = [".", ",", "!", "?", ":", ";", "'", "\u2019", '"', "(", ")", "-", "/", "&", "+"] as const;

/**
 * Whether a title has any of `words` as a whole word, ignoring case: the title is lowercased, every
 * WORD_BREAKS character becomes a space, and a word must sit between spaces or the ends ("vet's"
 * counts, "velvet" does not).
 */
export function titleMentionsWord(words: ReadonlyArray<string>, title: string): boolean {
  let t = title.toLowerCase();
  for (const b of WORD_BREAKS) t = t.split(b).join(" ");
  t = ` ${t} `;
  return words.some((w) => t.includes(` ${w} `));
}

/** Whether a title is an Omie job: an OMIE_WORDS word anywhere, or an OMIE_WHOLE_WORDS word on its own. */
export function isOmieJob(title: string): boolean {
  return titleMentions(OMIE_WORDS, title) || titleMentionsWord(OMIE_WHOLE_WORDS, title);
}

const atLeast = (n: number | null, min: number) => n !== null && n >= min;

/** Night Owl's hours: midnight to 5 am. */
export const NIGHT_OWL_HOURS = [0, 1, 2, 3, 4] as const;
/** Early Bird's hours: 5 am to 7 am (before 5 is Night Owl's). */
export const EARLY_BIRD_HOURS = [5, 6] as const;
/** Gold Hoarder: this much held at once. */
export const HOARD_GOLD = 1000;
/** Seven Sleeps: this many days in a row. */
export const STREAK_DAYS = 7;

export const ACHIEVEMENTS: readonly Achievement[] = [
  { key: "first-chore", name: "First Chore", line: "Finished a first task.", gold: 10, sprite: 0, earned: (f) => atLeast(f.tasksDone, 1) },
  { key: "dish-wizard", name: "Dish Wizard", line: "Ten dish jobs done.", gold: 20, sprite: 1, earned: (f) => atLeast(f.dishTasks, 10) },
  {
    key: "early-bird",
    name: "Early Bird",
    line: "A task done before 7 am.",
    gold: 15,
    sprite: 2,
    earned: (f) => atLeast(f.earlyDone, 1),
  },
  {
    key: "night-owl",
    name: "Night Owl",
    line: "A task done between midnight and 5 am.",
    gold: 15,
    sprite: 3,
    earned: (f) => atLeast(f.nightDone, 1),
  },
  { key: "quick-peon", name: "Quick Peon", line: "Ten quick bonuses.", gold: 20, sprite: 4, earned: (f) => atLeast(f.quickBonuses, 10) },
  { key: "sock-goblin", name: "Sock Goblin", line: "Ten laundry jobs done.", gold: 20, sprite: 5, earned: (f) => atLeast(f.laundryTasks, 10) },
  { key: "seven-sleeps", name: "Seven Sleeps", line: "A task done seven days in a row.", gold: 30, sprite: 6, earned: (f) => atLeast(f.dayStreak, STREAK_DAYS) },
  { key: "hundred-club", name: "Hundred Club", line: "A hundred tasks done.", gold: 30, sprite: 7, earned: (f) => atLeast(f.tasksDone, 100) },
  { key: "big-spender", name: "Big Spender", line: "Bought a first piece for your peon.", gold: 10, sprite: 8, earned: (f) => atLeast(f.cosmeticsBought, 1) },
  { key: "fancy-peon", name: "Fancy Peon", line: "Owns five pieces for your peon.", gold: 20, sprite: 9, earned: (f) => atLeast(f.cosmeticsOwned, 5) },
  { key: "gold-hoarder", name: "Gold Hoarder", line: "Held a thousand gold at once.", gold: 25, sprite: 10, earned: (f) => atLeast(f.balance, HOARD_GOLD) },
  { key: "crowned", name: "Crowned", line: "Peon of the Month.", gold: 30, sprite: 11, earned: (f) => atLeast(f.crowns, 1) },
  { key: "good-human", name: "Good Human", line: "Ten Omie jobs done.", gold: 20, sprite: 12, earned: (f) => atLeast(f.omieTasks, 10) },
];

/** How many cells public/ui/badge-sheet.png has (scripts/make-badges.mjs draws one per badge). */
export const BADGE_SPRITES = ACHIEVEMENTS.length;
/** One badge's size at 1x, in art pixels (and CSS px at its natural size). */
export const BADGE_SIZE = 24;

const BY_KEY = new Map<string, Achievement>(ACHIEVEMENTS.map((a) => [a.key, a]));

export function isAchievementKey(key: unknown): key is AchievementKey {
  return typeof key === "string" && BY_KEY.has(key);
}

export function achievement(key: string): Achievement | null {
  return BY_KEY.get(key) ?? null;
}

/** The badges the facts earn that are not in `have`, in the catalogue's order. */
export function newlyEarned(facts: AchievementFacts, have: Iterable<string>): Achievement[] {
  const held = new Set(have);
  return ACHIEVEMENTS.filter((a) => !held.has(a.key) && a.earned(facts));
}

/** Keys in the catalogue's order, unknown ones dropped (a newer server's badge on an older client). */
export function sortBadges(keys: Iterable<string>): AchievementKey[] {
  const held = new Set(keys);
  return ACHIEVEMENTS.filter((a) => held.has(a.key)).map((a) => a.key);
}

/** The count line beside the badges in Profile: "1 badge", "3 badges". */
export function badgeCountLine(n: number): string {
  return n === 1 ? "1 badge" : `${n} badges`;
}

/** The push for a new badge, to the peon who earned it only: the title says it, the body is a few words, no numbers (docs/product.md). */
export function badgePush(a: Achievement): { title: string; body: string } {
  return { title: `New badge: ${a.name}`, body: "It pays a little gold." };
}

/** One peon's badges as GET /api/achievements answers them. */
export interface PeonBadges {
  name: string;
  /** Badge keys in the catalogue's order. */
  badges: AchievementKey[];
}

/** GET /api/achievements: every peon in the household with at least one badge, most badges first. */
export interface AchievementsResponse {
  peons: PeonBadges[];
}

/** The badges of `name` in a response (names match ignoring case), or none. */
export function badgesOf(res: AchievementsResponse | null, name: string): AchievementKey[] {
  if (!res) return [];
  const key = name.trim().toLowerCase();
  return res.peons.find((p) => p.name.trim().toLowerCase() === key)?.badges ?? [];
}

/**
 * Days in a row ending on `today` (a "YYYY-MM-DD" local date) that appear in `days`. Zero when
 * `today` itself is missing. Pure so the streak rule can be tested without a clock or a zone.
 */
export function streakEndingOn(days: Iterable<string>, today: string): number {
  const have = new Set(days);
  let n = 0;
  let [y, m, d] = today.split("-").map(Number);
  for (;;) {
    const key = `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    if (!have.has(key)) return n;
    n++;
    const prev = new Date(Date.UTC(y, m - 1, d - 1));
    y = prev.getUTCFullYear();
    m = prev.getUTCMonth() + 1;
    d = prev.getUTCDate();
  }
}

/** Whether a local hour (0 to 23) is Early Bird's. */
export function isEarlyHour(hour: number): boolean {
  return (EARLY_BIRD_HOURS as readonly number[]).includes(hour);
}

/** Whether a local hour (0 to 23) is Night Owl's. */
export function isNightHour(hour: number): boolean {
  return (NIGHT_OWL_HOURS as readonly number[]).includes(hour);
}

/**
 * The longest run of consecutive "YYYY-MM-DD" local days in `days` (repeats and order do not
 * matter); zero for none. Seven Sleeps on a backfill: the badge says "A task done seven days in a
 * row", with no "today" in it, so any seven days in a row in the record earn it.
 */
export function longestStreak(days: Iterable<string>): number {
  const sorted = [...new Set(days)].sort();
  let best = 0;
  let run = 0;
  let prev = NaN;
  for (const day of sorted) {
    const [y, m, d] = day.split("-").map(Number);
    const t = Date.UTC(y, m - 1, d);
    if (Number.isNaN(t)) continue;
    run = t - prev === 86_400_000 ? run + 1 : 1;
    prev = t;
    if (run > best) best = run;
  }
  return best;
}

/** POST /api/achievements/backfill: how many peons were checked, and the badges and gold newly awarded. */
export interface BadgeBackfillResponse {
  checked: number;
  badges: number;
  gold: number;
}

// ---------- The trophy shelf (the character editor, under `achievementsUI`) ----------

/**
 * How many finished months `name` won Peon of the Month, from the Hall's months (newest first,
 * key "YYYY-MM"). The month still running (`currentKey`) is a race, not a crown, so it is left out.
 */
export function crownsWon(months: ReadonlyArray<{ key: string; name: string }>, name: string, currentKey: string): number {
  const me = name.trim().toLowerCase();
  return months.filter((m) => m.key < currentKey && m.name.trim().toLowerCase() === me).length;
}

/** The shelf's caption: "Peon of the Month, once", "twice", "3 times". Empty for none (no shelf). */
export function trophyCaption(n: number): string {
  if (n <= 0) return "";
  if (n === 1) return "Peon of the Month, once";
  if (n === 2) return "Peon of the Month, twice";
  return `Peon of the Month, ${n} times`;
}

/** Crowns drawn on the shelf at most; past it the caption carries the count. */
export const SHELF_MAX = 12;

// ---------- Showing them (both clients) ----------

/** Badges drawn in Profile's one row at most; the rest are "+n" (the count line says the total). */
export const PROFILE_BADGE_MAX = 9;

/** The badges a row draws and how many more there are: the newest-earned order is not kept, so the catalogue's first `max`. */
export function visibleBadges(keys: ReadonlyArray<AchievementKey>, max: number): { shown: AchievementKey[]; more: number } {
  return { shown: keys.slice(0, max), more: Math.max(0, keys.length - max) };
}

// ---------- Wearing one (Your peon's Badges section; owner, 2026-10-05) ----------

/**
 * How many badges a peon wears at once: one, sitting over the shirt's stitched patch (owner,
 * 2026-10-05: "Rather than 3 badges should just be able to set one badge that sits on the peons
 * shirt where the existing emblem is"). The stored list stays a list (migration 0047) of at most one.
 */
export const WORN_BADGE_MAX = 1;

/** A worn list as stored and drawn: known keys only, the first one. */
export function normalizeWornBadges(list: ReadonlyArray<unknown>): AchievementKey[] {
  const out: AchievementKey[] = [];
  for (const k of list) {
    if (out.length >= WORN_BADGE_MAX) break;
    if (isAchievementKey(k) && !out.includes(k)) out.push(k);
  }
  return out;
}

/** The worn list after a tap on `key`: off if it is the one worn, else it alone (a swap). */
export function toggleWornBadge(worn: ReadonlyArray<AchievementKey>, key: AchievementKey): AchievementKey[] {
  return worn.includes(key) ? [] : [key];
}

/**
 * The worn badge's pin (scripts/make-badge-pins.mjs, public/ui/badge-pins.png): 8x8 art pixels in
 * the 46x44 frame every portrait layer shares (docs/peon-look.md: the crowned portrait's canvas; the
 * 46x38 mascot sits at its rows 4 to 41). It covers the shirt's stitched leather patch
 * (scripts/make-mascot.mjs "a patch": the mascot's columns 6 to 12, rows 30 to 35, outline included,
 * the frame's rows 34 to 39): the pin's box is columns 6 to 13, frame rows 33 to 40 (the mascot's 29
 * to 36). The patch's leather and stitches are all under the pin's opaque rows; only its two left
 * outline corners, the pin's own outline colour, meet the octagon's cut corners. Column 13 is plain
 * shirt, clear of the strap's dark edge (column 14) and far from the collar; row 40 is above the hem.
 */
export const BADGE_PIN = { size: 8, left: 6, top: 33, frameW: 46, frameH: 44 } as const;

/** The pin's place in the 46x44 frame, in art pixels, and its cell in the pin sheet. */
export interface BadgePinSpot {
  key: AchievementKey;
  sprite: number;
  x: number;
  y: number;
}

/** Where the worn badge's pin goes: over the patch, or nowhere. Unknown keys and a second are dropped. */
export function badgePinSpots(worn: ReadonlyArray<unknown>): BadgePinSpot[] {
  return normalizeWornBadges(worn).map((key) => ({ key, sprite: achievement(key)?.sprite ?? 0, x: BADGE_PIN.left, y: BADGE_PIN.top }));
}

/** The Badges section's count line: "4 of 13 earned". */
export function badgeCatalogueLine(earned: number, total: number = ACHIEVEMENTS.length): string {
  return `${earned} of ${total} earned`;
}

/** One badge of the Badges section: the catalogue's entry, whether this peon has it, and whether it is worn. */
export interface BadgeTile {
  key: AchievementKey;
  name: string;
  /** What earned it; on a badge not earned yet it reads as how to get it. */
  line: string;
  sprite: number;
  earned: boolean;
  worn: boolean;
}

/** The Badges section: every badge in the catalogue's order, each earned or not, worn or not. */
export function badgeCatalogue(earned: Iterable<string>, worn: ReadonlyArray<string>): BadgeTile[] {
  const have = new Set(earned);
  return ACHIEVEMENTS.map((a) => ({ key: a.key, name: a.name, line: a.line, sprite: a.sprite, earned: have.has(a.key), worn: worn.includes(a.key) }));
}

/**
 * What a tap on a badge in the Badges section does: an earned one is worn in place of whatever was
 * (a swap, one save), the worn one is taken off and the patch shows again; a badge not earned yet
 * says its name and its line, which is how to get it ("Dish Wizard: Ten dish jobs done."), since the
 * tile is only the badge. Wearing is free.
 */
export function badgeTap(
  key: AchievementKey,
  earned: Iterable<string>,
  worn: ReadonlyArray<AchievementKey>,
): { kind: "wear"; badges: AchievementKey[] } | { kind: "say"; line: string } {
  if (!new Set(earned).has(key)) {
    const a = achievement(key);
    return { kind: "say", line: a ? `${a.name}: ${a.line}` : "" };
  }
  return { kind: "wear", badges: toggleWornBadge(worn, key) };
}

/** A badge tile's accessible name, earned or not (the dimming says which): "Dish Wizard. Ten dish jobs done." */
export function badgeTileLabel(tile: Pick<BadgeTile, "name" | "line">): string {
  return `${tile.name}. ${tile.line}`;
}

/** The Badges section's quiet line when nothing else needs saying: the badge worn, or "None". */
export function wornBadgesCaption(worn: ReadonlyArray<string>): string {
  const names = normalizeWornBadges(worn).map((k) => achievement(k)?.name ?? k);
  return names.length ? names.join(", ") : "None";
}

/** The server's refusal of a badge this peon has not earned. */
export const BADGE_NOT_EARNED = "You haven't earned that one yet.";

/** A badge's accessible name: "Dish Wizard. Ten dish jobs done." */
export function badgeLabel(key: string): string {
  const a = achievement(key);
  return a ? `${a.name}. ${a.line}` : key;
}
